/* eslint-disable no-nested-ternary */
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
    Box,
    Button,
    Typography,
    ButtonGroup,
    TextField,
    Tooltip,
    FormControl,
    Select,
    Avatar,
    Switch,
    CircularProgress,
    Chip,
    MenuItem,
    Popper,
    Paper,
    ClickAwayListener,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { useSelector, dispatch } from 'src/redux/store';
import {
    addAppointment,
    consultationAttended,
    StartingTreatment,
    CompleteTreatment,
    FailedToAttendAppointment,
    beginArchiveLead,
    setAction,
} from 'src/redux/slices/lead';
import { getPracticeUserList } from 'src/redux/slices/practiceUser';
import { useLeadTableHelper } from 'src/hooks';
import useGoogleReviewSend from 'src/hooks/useGoogleReviewSend';
import { generateReasonsLabel } from 'src/utils/getReferralData';
import { followUpSelection } from 'src/constants/appointmentReason';
import { Iconify } from '..';
import GoogleReviewPreviewDialog from './GoogleReviewPreviewDialog';

const ACCENT = '#00A76F';

// Sidebar-local mirror of the canonical unified split-button SHAPE from
// `src/sections/tasks/statusPrimaryAction.js` (PRIMARY_BUTTON_SX /
// SPLIT_TRIGGER_SX): an `inline-flex` row of a contained primary fused to a
// 32px contained dropdown trigger via the 8px corner-radius split.
//
// We deliberately do NOT import that shared module: its colour is #0078FF
// (blue) and it is shared with the leads-table board rows, which are out of
// scope this round. Here we apply the same SHAPE but override the colour to
// CRM green #00A76F. The WhatsApp channel variant keeps its own #25D366 (see
// sidebarPrimarySx usage below) — unchanged from the previous design.
//
// Width: the canonical table button is a fixed 150px pill, but in the ~370px
// task slideout / action panel that looks orphaned, so the sidebar primary
// fills the available width (flex:1) with the trigger fused on the right.
//
// Primary button of the split. Uses the same 44px height and 10px radius as the
// Archive button so the action row stays visually balanced. `bg` is passed in so
// the WhatsApp variant can swap to #25D366 without a second style object.
const sidebarPrimarySx = (bg, hoverBg, hasTrigger) => ({
    height: 44,
    flex: 1,
    minWidth: 0,
    fontSize: '14px',
    fontWeight: 700,
    textTransform: 'none',
    justifyContent: 'flex-start',
    color: '#fff',
    bgcolor: `${bg} !important`,
    '&:hover': { bgcolor: `${hoverBg} !important` },
    px: 1.5,
    borderRadius: hasTrigger ? '10px 0 0 10px' : '10px',
    boxShadow: 'none',
    transition: 'background-color 0.2s ease',
});

// Dropdown trigger of the split. Matches the 44px action height.
const sidebarTriggerSx = (bg, hoverBg) => ({
    minWidth: 32,
    width: 32,
    height: 44,
    px: 0,
    color: '#fff',
    bgcolor: `${bg} !important`,
    '&:hover': { bgcolor: `${hoverBg} !important` },
    borderRadius: '0 10px 10px 0',
    borderLeft: '1px solid rgba(255,255,255,0.3) !important',
    boxShadow: 'none',
    transition: 'background-color 0.2s ease',
});

const getDatePresets = () => [
    { label: 'Today', value: moment().set({ hour: 12, minute: 0, second: 0 }) },
    { label: 'Tomorrow', value: moment().add(1, 'day').set({ hour: 12, minute: 0, second: 0 }) },
    { label: 'Next Mon', value: moment().day(8).set({ hour: 12, minute: 0, second: 0 }) },
    { label: 'Next Week', value: moment().add(1, 'week').set({ hour: 12, minute: 0, second: 0 }) },
];

const ARCHIVE_REASON_ICONS = {
    ANOTHER_PRACTICE: 'mdi:hospital-building',
    APPOINTMENT_RELATED: 'mdi:calendar-remove-outline',
    CHANGED_MIND: 'mdi:head-sync-outline',
    CLINICALLY_NOT_SUITABLE: 'mdi:stethoscope',
    DUPLICATE_ENTRY: 'mdi:content-copy',
    NO_RESPONSE: 'mdi:phone-off-outline',
    OTHER: 'mdi:dots-horizontal-circle-outline',
    PRACTICE_TOO_FAR: 'mdi:map-marker-off-outline',
    TOO_EXPENSIVE: 'mdi:currency-gbp',
    TREATMENT_RELATED: 'mdi:tooth-outline',
    WANT_NHS: 'mdi:shield-cross-outline',
};

const getJourneyIndex = (status) => {
    const map = {
        NEW: 0, TODO: 0, CALLBACK: 0,
        CONSULTATION_BOOKED: 1, FAILED_TO_ATTEND_CONSULTATION: 1,
        CONSULTATION_ATTENDED: 2, TREATMENT_STARTED: 3, CLOSED: 4,
    };
    return map[status] ?? -1;
};

const getForwardActions = (currentStatus) => {
    const currentIndex = getJourneyIndex(currentStatus);
    if (currentIndex === -1) return [];
    const actions = [];
    // Follow Up sits at the TOP of every non-CLOSED status because "call /
    // contact the lead" is the most-common day-to-day action regardless of
    // FSM stage. Status-aware routing (OpenContactLeadPopup for NEW,
    // OpenAttendedPopup for CONSULTATION_ATTENDED, OpenUnAttendedPopup for FTA)
    // is handled in TaskBodyView.handleActionZoneSuccess.
    if (currentIndex < 4) {
        actions.push({ key: 'FOLLOW_UP', label: 'Follow Up', subtitle: 'Call / contact this lead', icon: 'solar:phone-calling-bold-duotone', color: ACCENT, section: 'Quick Actions' });
    }
    if (currentStatus === 'CONSULTATION_BOOKED' || currentStatus === 'FAILED_TO_ATTEND_CONSULTATION') {
        actions.push({ key: 'ATTENDED', label: 'Attended', subtitle: 'Mark consultation as attended', icon: 'solar:check-circle-bold-duotone', color: '#22C55E', section: 'Attendance' });
        // Don't offer "Failed to Attend" for a lead that has already failed to
        // attend — the backend no-ops it and the control is meaningless.
        if (currentStatus !== 'FAILED_TO_ATTEND_CONSULTATION') {
            actions.push({ key: 'FAILED_TO_ATTEND', label: 'Failed to Attend', subtitle: 'Patient did not show up', icon: 'solar:close-circle-bold-duotone', color: '#FF5630', section: 'Attendance' });
        }
    }
    if (currentIndex < 1) actions.push({ key: 'BOOK', label: 'Book Consultation', subtitle: 'Schedule a new appointment', icon: 'solar:calendar-bold-duotone', color: '#0078FF', section: 'Quick Actions' });
    if (currentIndex < 2 && currentStatus !== 'CONSULTATION_BOOKED' && currentStatus !== 'FAILED_TO_ATTEND_CONSULTATION' && currentStatus !== 'NEW') {
        actions.push({ key: 'ATTENDED', label: 'Record Attended', subtitle: 'Mark consultation as attended', icon: 'solar:check-circle-bold-duotone', color: '#22C55E', section: 'Quick Actions' });
    }
    if (currentIndex < 3) actions.push({ key: 'START_TREATMENT', label: 'Start Treatment', subtitle: 'Begin the treatment plan', icon: 'solar:heart-pulse-bold-duotone', color: '#FFAB00', section: 'Quick Actions' });
    if (currentIndex < 4) actions.push({ key: 'COMPLETE', label: 'Complete Treatment', subtitle: 'Mark treatment as finished', icon: 'solar:verified-check-bold-duotone', color: '#00A76F', section: 'Quick Actions' });
    return actions;
};

const getNextStepOptions = (completed) => {
    const opts = [];
    switch (completed) {
        case 'BOOK':
            opts.push({ key: 'FOLLOW_UP', label: 'Set Follow-Up Reminder', icon: 'solar:bell-bold-duotone', color: '#0078FF' });
            opts.push({ key: 'ARCHIVE', label: 'Archive Lead', icon: 'solar:archive-bold-duotone', color: '#FF5630' });
            break;
        case 'ATTENDED':
            opts.push({ key: 'START_TREATMENT', label: 'Start Treatment', icon: 'solar:heart-pulse-bold-duotone', color: '#FFAB00' });
            opts.push({ key: 'FOLLOW_UP', label: 'Set Follow-Up Reminder', icon: 'solar:bell-bold-duotone', color: '#8E33FF' });
            opts.push({ key: 'COMPLETE', label: 'Complete Treatment', icon: 'solar:verified-check-bold-duotone', color: '#00A76F' });
            opts.push({ key: 'ARCHIVE', label: 'Archive Lead', icon: 'solar:archive-bold-duotone', color: '#FF5630' });
            break;
        case 'START_TREATMENT':
            opts.push({ key: 'COMPLETE', label: 'Complete Treatment', icon: 'solar:verified-check-bold-duotone', color: '#00A76F' });
            opts.push({ key: 'FOLLOW_UP', label: 'Set Follow-Up Reminder', icon: 'solar:bell-bold-duotone', color: '#8E33FF' });
            opts.push({ key: 'ARCHIVE', label: 'Archive Lead', icon: 'solar:archive-bold-duotone', color: '#FF5630' });
            break;
        case 'COMPLETE':
            opts.push({ key: 'GOOGLE_REVIEW', label: 'Request Google Review', icon: 'solar:star-bold-duotone', color: '#FFAB00' });
            opts.push({ key: 'FOLLOW_UP', label: 'Set Follow-Up Reminder', icon: 'solar:bell-bold-duotone', color: '#8E33FF' });
            opts.push({ key: 'ARCHIVE', label: 'Archive Lead', icon: 'solar:archive-bold-duotone', color: '#FF5630' });
            break;
        default:
            break;
    }
    return opts;
};

// Pick the best default treatment when no explicit one is provided.
// Filter to treatments the lead actually has, then prefer non-CLOSED, then most-recently-updated.
const pickDefaultTreatment = (treatments) => {
    if (!Array.isArray(treatments) || treatments.length === 0) return null;
    const active = treatments.filter((t) => t.status !== 'CLOSED' && t.status !== 'ARCHIVED');
    const pool = active.length > 0 ? active : treatments;
    return [...pool].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
};

const LeadActionZone = ({ lead, leadTreatment: propLeadTreatment, practiceId, onSuccess, onActionComplete, taskMethod, bookRoutesToWizard }) => {
    // Build the list of switchable treatments from the lead. If the parent passed
    // an explicit leadTreatment AND lead has multiple treatments, both are visible
    // in the picker — the explicit one is the default selection.
    const availableTreatments = Array.isArray(lead?.lead_treatments) && lead.lead_treatments.length > 0
        ? lead.lead_treatments
        : (propLeadTreatment ? [propLeadTreatment] : []);

    const [selectedTreatmentId, setSelectedTreatmentId] = useState(() => {
        if (propLeadTreatment?.id) return propLeadTreatment.id;
        const def = pickDefaultTreatment(availableTreatments);
        return def?.id || null;
    });

    // Effective treatment is what every dialog/dispatch operates on.
    // Aliased to `leadTreatment` so the existing handler body (20+ refs) Just Works.
    const leadTreatment = availableTreatments.find((t) => t.id === selectedTreatmentId)
        || propLeadTreatment
        || pickDefaultTreatment(availableTreatments);

    // Re-sync selection when prop or lead changes (e.g. after a refresh).
    useEffect(() => {
        const next = propLeadTreatment?.id || pickDefaultTreatment(availableTreatments)?.id || null;
        if (next && next !== selectedTreatmentId) setSelectedTreatmentId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propLeadTreatment?.id, lead?.id]);

    const showTreatmentSwitcher = availableTreatments.length > 1;

    const { practice } = useSelector((state) => state.practice);
    const { practiceUser: practiceUserData } = useSelector((state) => state.practiceUser);
    const { archiveReasons: rawArchiveReasons } = useSelector((state) => state.schema);
    const { enqueueSnackbar } = useSnackbar();

    const followUpAnchorRef = useRef(null);

    const [followUpMenuOpen, setFollowUpMenuOpen] = useState(false);
    const [showArchiveView, setShowArchiveView] = useState(false);
    const [archiveSubmitting, setArchiveSubmitting] = useState(false);
    const [archiveReason, setArchiveReason] = useState('');
    const [archiveNotes, setArchiveNotes] = useState('');
    const [archiveCommunication, setArchiveCommunication] = useState('No');
    const [activeQuickAction, setActiveQuickAction] = useState(null);
    const [quickActionDatetime, setQuickActionDatetime] = useState(null);
    const [depositConfirmed, setDepositConfirmed] = useState(false);
    const [quickActionPaymentMode, setQuickActionPaymentMode] = useState('');
    const [quickActionLoading, setQuickActionLoading] = useState(false);
    const [quickActionBookedWith, setQuickActionBookedWith] = useState('');
    const [quickActionAssignedTo, setQuickActionAssignedTo] = useState('');
    const [ftaOutcome, setFtaOutcome] = useState('');
    const [ftaEmailSending, setFtaEmailSending] = useState(false);
    const [completedQuickAction, setCompletedQuickAction] = useState(null);
    const { nearestPastData: nearestPastDataV2, nearestData: nearestDataV2 } = useLeadTableHelper('ConsultantBooked');
    const leadAppointments = nearestDataV2(leadTreatment?.appointments) || nearestPastDataV2(leadTreatment?.appointments);

    const {
        templateData: googleReviewTemplateDetail,
        isSending: googleReviewSending,
        previewOpen: googleReviewPreviewOpen,
        openPreview: handleOpenGoogleReviewPreview,
        closePreview: handleCloseGoogleReviewPreview,
        sendRequest: sendGoogleReviewRequest,
    } = useGoogleReviewSend();

    const isPaymentMandatory = practice?.preferences?.is_payment_enforced_before_treatment;

    const archiveReasonsList = (() => {
        let reasons = rawArchiveReasons || [];
        if (typeof reasons === 'string') reasons = JSON.parse(reasons);
        return reasons
            .filter((v) => v !== 'UNABLE_TO_CONTACT' && v !== 'NHS_WAITING_LIST' && v !== 'DUPLICATE' && !followUpSelection.includes(v))
            .sort()
            .map((value) => ({ label: value === 'WANT_NHS' ? 'Want NHS' : generateReasonsLabel(value), value }));
    })();

    const patientName = `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim();
    const forwardActions = getForwardActions(leadTreatment?.status);
    // Arrow is ALWAYS visible — V1 parity. When no quick actions are valid
    // (e.g. treatment-less lead), the dropdown shows a hint placeholder.
    const hasDropdown = true;
    const singleAction = forwardActions.length === 1 ? forwardActions[0] : null;

    const handleQuickAction = (actionKey) => {
        setFollowUpMenuOpen(false);
        // Task-detail context: the BOOK action must open the same FollowUpV2
        // booking wizard the leads table uses (ContactLeadPopupRouter →
        // FollowUpV2), NOT this component's inline quick-book date-picker form.
        // The parent (TaskBodyView) wires `bookRoutesToWizard` and handles the
        // 'BOOK_VIA_WIZARD' signal in handleActionZoneSuccess. This is gated to
        // the task-detail mount only — every other LeadActionZone call site
        // leaves `bookRoutesToWizard` unset and keeps the inline form.
        if (actionKey === 'BOOK' && bookRoutesToWizard) {
            onSuccess('BOOK_VIA_WIZARD');
            return;
        }
        setActiveQuickAction(actionKey);
        setQuickActionDatetime(null);
        setDepositConfirmed(false);
        setQuickActionPaymentMode('');
        setQuickActionBookedWith('');
        setQuickActionAssignedTo('');
        setFtaOutcome('');
        if ((actionKey === 'BOOK' || actionKey === 'START_TREATMENT') && practiceId) {
            dispatch(getPracticeUserList(practiceId));
        }
    };

    const handleQuickActionCancel = () => {
        const wasCompleted = !!completedQuickAction;
        setActiveQuickAction(null);
        setCompletedQuickAction(null);
        setQuickActionDatetime(null);
        setDepositConfirmed(false);
        setQuickActionPaymentMode('');
        setQuickActionLoading(false);
        setQuickActionBookedWith('');
        setQuickActionAssignedTo('');
        setFtaOutcome('');
        setFtaEmailSending(false);
        if (wasCompleted) onSuccess('CLOSE');
    };

    const isQuickActionDisabled = () => {
        if (quickActionLoading) return true;
        if (activeQuickAction === 'BOOK' && !quickActionDatetime) return true;
        if (activeQuickAction === 'START_TREATMENT' && isPaymentMandatory && (!depositConfirmed || !quickActionPaymentMode)) return true;
        if (activeQuickAction === 'ATTENDED' && !leadTreatment?.id) return true;
        if (activeQuickAction === 'FAILED_TO_ATTEND' && (!leadAppointments?.[0]?.id || !ftaOutcome)) return true;
        return false;
    };

    const handleQuickActionConfirm = () => {
        const leadId = leadTreatment?.lead;
        const treatmentId = leadTreatment?.id;
        setQuickActionLoading(true);

        const justCompleted = activeQuickAction;

        const onDone = () => {
            setQuickActionLoading(false);
            setActiveQuickAction(null);
            setCompletedQuickAction(justCompleted);
            if (onActionComplete) onActionComplete(justCompleted);
            onSuccess(justCompleted);
        };

        switch (activeQuickAction) {
            case 'BOOK': {
                const isNewLead = leadTreatment?.status === 'NEW';
                const bookOnDone = () => {
                    setQuickActionLoading(false);
                    setActiveQuickAction(null);
                    if (onActionComplete) onActionComplete('BOOK');
                    if (isNewLead) {
                        onSuccess('BOOK_CLOSE');
                    } else {
                        setCompletedQuickAction(justCompleted);
                        onSuccess('BOOK');
                    }
                };
                const bookPayload = {
                    datetime: `${moment(quickActionDatetime).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                    lead_treatment: treatmentId,
                };
                const assigneeIds = [quickActionBookedWith, quickActionAssignedTo].filter(Boolean);
                if (assigneeIds.length > 0) bookPayload.assignees = assigneeIds;
                dispatch(addAppointment(bookPayload, practiceId, leadId, bookOnDone, 'AddAppointment'));
                break;
            }
            case 'ATTENDED': {
                dispatch(consultationAttended(practiceId, leadId, treatmentId, onDone, 'TreatmentAccordion', {
                    onCancel: () => setQuickActionLoading(false),
                }));
                break;
            }
            case 'START_TREATMENT': {
                // Defensive: a Contact Lead popup used earlier in this session may
                // have left the global startTreatment action flag set (normally
                // cleared on consumption/close, but this guards against any gap).
                // This quick action starts treatment itself via startTreatmentAction
                // below; if that stale flag were still set, addAppointment() would
                // ALSO independently fire StartingTreatment() off its own (possibly
                // unrelated) newLeadDetail snapshot — a second, wrong-treatment
                // double-start. Clear it first so only startTreatmentAction runs.
                dispatch(setAction(null));
                let afterStart = onDone;
                if (quickActionDatetime) {
                    const apptPayload = {
                        datetime: `${moment(quickActionDatetime).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                        lead_treatment: treatmentId,
                        type: 'TREATMENT',
                    };
                    const apptAssignees = [quickActionBookedWith, quickActionAssignedTo].filter(Boolean);
                    if (apptAssignees.length > 0) apptPayload.assignees = apptAssignees;
                    afterStart = () => dispatch(addAppointment(apptPayload, practiceId, leadId, onDone, 'AddAppointment', () => setQuickActionLoading(false)));
                }
                dispatch(StartingTreatment(
                    { lead: leadId, id: treatmentId, treatment: leadTreatment?.treatment, custom_price: leadTreatment?.custom_price },
                    practiceId, afterStart, 'startingTreatmentFinish', { onCancel: () => setQuickActionLoading(false) }
                ));
                break;
            }
            case 'COMPLETE':
                dispatch(CompleteTreatment(
                    { treatment: leadTreatment?.treatment, custom_price: leadTreatment?.custom_price },
                    leadId, treatmentId, practiceId, onDone
                ));
                break;
            case 'FAILED_TO_ATTEND': {
                const appt = leadAppointments?.[0];
                if (!appt?.id || !ftaOutcome) { setQuickActionLoading(false); return; }
                // Belt-and-braces against stale rows / double-clicks: a lead that
                // is already FAILED_TO_ATTEND_CONSULTATION can't fail to attend again.
                if (leadTreatment?.status === 'FAILED_TO_ATTEND_CONSULTATION') { setQuickActionLoading(false); return; }
                const selectedOutcome = ftaOutcome;
                const ftaOnDone = () => {
                    setQuickActionLoading(false);
                    if (onActionComplete) onActionComplete('FAILED_TO_ATTEND');
                    onSuccess('FAILED_TO_ATTEND');
                    setFtaEmailSending(true);
                    setTimeout(() => {
                        setFtaEmailSending(false);
                        setActiveQuickAction(null);
                        if (selectedOutcome === 'UNABLE_TO_CONTACT') {
                            onSuccess('CLOSE');
                        } else if (selectedOutcome === 'RESCHEDULE') {
                            setTimeout(() => handleQuickAction('BOOK'), 100);
                        } else if (selectedOutcome === 'ARCHIVE') {
                            setShowArchiveView(true);
                        }
                    }, 2500);
                };
                dispatch(FailedToAttendAppointment(
                    { contacted_via: appt.contacted_via, datetime: appt.datetime, lead_treatment: appt.lead_treatment, method: appt.method, note: appt.note },
                    leadId, appt.id, practiceId, ftaOnDone, 'failedAttendTreatment'
                ));
                break;
            }
            default:
                setQuickActionLoading(false);
                break;
        }
    };

    const handleArchiveSubmit = async () => {
        if (archiveSubmitting) return;
        if (!archiveReason) { enqueueSnackbar('Please select an archive reason', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } }); return; }
        if (!archiveNotes?.trim()) { enqueueSnackbar('Please add archive notes', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } }); return; }
        const archiveLeadId = lead?.id || leadTreatment?.lead;
        if (!archiveLeadId || !practiceId) {
            enqueueSnackbar('Cannot archive: lead not identified. Please close and reopen.', { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            return;
        }
        setArchiveSubmitting(true);
        try {
            await dispatch(beginArchiveLead({
                leadId: archiveLeadId,
                practiceId,
                leadTreatmentId: leadTreatment?.id || null,
                reason: archiveReason,
                note: archiveNotes,
                isOpenForCommunication: archiveCommunication === 'Yes',
                source: 'tasks-v2:lead-action-zone',
                handleClose: handleArchiveClose,
            }));
        } finally {
            setArchiveSubmitting(false);
        }
    };

    const handleArchiveClose = () => {
        setShowArchiveView(false);
        setArchiveSubmitting(false);
        setArchiveReason('');
        setArchiveNotes('');
        setArchiveCommunication('No');
        onSuccess('ARCHIVE');
    };

    const handleNextStep = (key) => {
        setCompletedQuickAction(null);
        if (key === 'ARCHIVE') {
            setShowArchiveView(true);
        } else if (key === 'GOOGLE_REVIEW') {
            sendGoogleReviewRequest(
                {
                    leadId: lead?.id || leadTreatment?.lead,
                    leadTreatmentId: leadTreatment?.id,
                    leadData: lead,
                },
                { onSuccess: () => onSuccess('CLOSE') }
            );
        } else if (key === 'FOLLOW_UP') {
            onSuccess('FOLLOW_UP');
        } else {
            // Another quick action from the next-step list
            handleQuickAction(key);
        }
    };

    return (
        <Box sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid #e8f5e9', bgcolor: '#f8faf9' }}>
            {/* FTA email sending animation */}
            {ftaEmailSending ? (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid', borderColor: alpha('#0078FF', 0.3), animation: 'ftaPulse 1.5s ease-out infinite', '@keyframes ftaPulse': { '0%': { transform: 'scale(1)', opacity: 1 }, '100%': { transform: 'scale(1.8)', opacity: 0 } } }} />
                        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid', borderColor: alpha('#0078FF', 0.3), animation: 'ftaPulse 1.5s ease-out infinite 0.5s' }} />
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: alpha('#0078FF', 0.1) }}>
                            <Iconify icon="solar:letter-bold-duotone" width={28} sx={{ color: '#0078FF', animation: 'ftaFloat 1s ease-in-out infinite', '@keyframes ftaFloat': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-4px)' } } }} />
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>Sending email notification...</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888' }}>Notifying <strong>{lead?.first_name || 'the patient'}</strong> about the missed appointment</Typography>
                    <Box sx={{ mt: 2, mx: 'auto', width: '80%', height: 4, bgcolor: alpha('#0078FF', 0.1), borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', bgcolor: '#0078FF', borderRadius: 2, animation: 'ftaProgress 2.5s ease-out forwards', '@keyframes ftaProgress': { '0%': { width: '0%' }, '70%': { width: '85%' }, '100%': { width: '100%' } } }} />
                    </Box>
                </Box>
            ) : googleReviewSending ? (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid', borderColor: alpha('#FFAB00', 0.3), animation: 'reviewPulse 1.5s ease-out infinite', '@keyframes reviewPulse': { '0%': { transform: 'scale(1)', opacity: 1 }, '100%': { transform: 'scale(1.8)', opacity: 0 } } }} />
                        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid', borderColor: alpha('#FFAB00', 0.3), animation: 'reviewPulse 1.5s ease-out infinite 0.5s' }} />
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: alpha('#FFAB00', 0.1) }}>
                            <Iconify icon="solar:star-bold-duotone" width={28} sx={{ color: '#FFAB00', animation: 'reviewFloat 1s ease-in-out infinite', '@keyframes reviewFloat': { '0%, 100%': { transform: 'translateY(0) rotate(0deg)' }, '50%': { transform: 'translateY(-4px) rotate(10deg)' } } }} />
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>Sending review request...</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888' }}>Asking <strong>{lead?.first_name || 'the patient'}</strong> for a Google review</Typography>
                    <Box sx={{ mt: 2, mx: 'auto', width: '80%', height: 4, bgcolor: alpha('#FFAB00', 0.1), borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', bgcolor: '#FFAB00', borderRadius: 2, animation: 'reviewProgress 2s ease-out forwards', '@keyframes reviewProgress': { '0%': { width: '0%' }, '70%': { width: '85%' }, '100%': { width: '100%' } } }} />
                    </Box>
                </Box>
            ) : activeQuickAction ? (
                <>
                    {/* Inline action title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Iconify icon={forwardActions.find((a) => a.key === activeQuickAction)?.icon || 'mdi:flash-outline'} width={18} sx={{ color: ACCENT }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>
                            {forwardActions.find((a) => a.key === activeQuickAction)?.label || 'Quick Action'}
                        </Typography>
                    </Box>

                    {/* BOOK */}
                    {activeQuickAction === 'BOOK' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.5 }}>
                            <DateTimePicker
                                label="Consultation date & time"
                                ampm={false}
                                value={quickActionDatetime}
                                minDateTime={new Date()}
                                onChange={(v) => setQuickActionDatetime(v)}
                                slotProps={{ textField: { fullWidth: true, size: 'small', sx: { '.MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', '& fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: ACCENT } } } } }}
                            />
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                {getDatePresets().filter((p) => p.value.isAfter(moment())).map((preset) => (
                                    <Chip
                                        key={preset.label}
                                        label={preset.label}
                                        size="small"
                                        variant={quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? 'filled' : 'outlined'}
                                        onClick={() => setQuickActionDatetime(preset.value.toDate())}
                                        sx={{ fontSize: '11px', fontWeight: 600, height: 26, borderRadius: '6px', borderColor: quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? ACCENT : '#ddd', bgcolor: quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? alpha(ACCENT, 0.1) : 'transparent', color: quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? ACCENT : '#666', '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.06) } }}
                                    />
                                ))}
                            </Box>
                            <FormControl fullWidth size="small">
                                <Select displayEmpty value={quickActionBookedWith} onChange={(e) => setQuickActionBookedWith(e.target.value)}
                                    renderValue={(val) => { if (!val) return <Typography sx={{ fontSize: '13px', color: '#aaa' }}>Booked with (optional)</Typography>; const u = practiceUserData?.results?.find((p) => p.id === val); return <Typography sx={{ fontSize: '13px' }}>{u?.user?.first_name} {u?.user?.last_name}</Typography>; }}
                                    sx={{ borderRadius: '8px', fontSize: '13px', '& fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: ACCENT } }}>
                                    <MenuItem value=""><Typography sx={{ fontSize: '13px', color: '#999' }}>None</Typography></MenuItem>
                                    {practiceUserData?.results?.filter((p) => !p?.user?.is_superuser && p?.is_enabled)?.map((p) => (
                                        <MenuItem key={p.id} value={p.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ width: 24, height: 24 }} src={p?.user?.avatar || ''} /><Typography sx={{ fontSize: '13px' }}>{p?.user?.first_name} {p?.user?.last_name}</Typography></Box></MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <Select displayEmpty value={quickActionAssignedTo} onChange={(e) => setQuickActionAssignedTo(e.target.value)}
                                    renderValue={(val) => { if (!val) return <Typography sx={{ fontSize: '13px', color: '#aaa' }}>Assigned to (optional)</Typography>; const u = practiceUserData?.results?.find((p) => p.id === val); return <Typography sx={{ fontSize: '13px' }}>{u?.user?.first_name} {u?.user?.last_name}</Typography>; }}
                                    sx={{ borderRadius: '8px', fontSize: '13px', '& fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: ACCENT } }}>
                                    <MenuItem value=""><Typography sx={{ fontSize: '13px', color: '#999' }}>None</Typography></MenuItem>
                                    {practiceUserData?.results?.filter((p) => !p?.user?.is_superuser && p?.is_enabled)?.map((p) => (
                                        <MenuItem key={p.id} value={p.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ width: 24, height: 24 }} src={p?.user?.avatar || ''} /><Typography sx={{ fontSize: '13px' }}>{p?.user?.first_name} {p?.user?.last_name}</Typography></Box></MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* ATTENDED */}
                    {activeQuickAction === 'ATTENDED' && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontSize: '13px', color: '#555' }}>Choose and confirm the consultation attendance details.</Typography>
                        </Box>
                    )}

                    {/* FAILED_TO_ATTEND */}
                    {activeQuickAction === 'FAILED_TO_ATTEND' && (
                        <Box sx={{ mb: 1.5 }}>
                            {leadAppointments?.[0]?.id ? (
                                <>
                                    <Typography sx={{ fontSize: '13px', color: '#555', mb: 1.25 }}>
                                        <strong>{lead?.first_name || 'Patient'}</strong> failed to attend. What happened?
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        {[
                                            { key: 'UNABLE_TO_CONTACT', label: 'Unable to Contact', subtitle: 'Tried calling the patient', icon: 'solar:phone-calling-bold-duotone', color: '#FF6C40' },
                                            { key: 'RESCHEDULE', label: 'Reschedule', subtitle: 'Spoken to the patient', icon: 'solar:calendar-bold-duotone', color: '#0078FF' },
                                            { key: 'ARCHIVE', label: 'Archive Lead', subtitle: 'No longer interested', icon: 'solar:archive-bold-duotone', color: '#FF5630' },
                                        ].map((opt) => (
                                            <Box key={opt.key} onClick={() => setFtaOutcome(opt.key)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: '8px', cursor: 'pointer', border: ftaOutcome === opt.key ? `2px solid ${opt.color}` : '1px solid #e8e8e8', bgcolor: ftaOutcome === opt.key ? alpha(opt.color, 0.06) : 'transparent', transition: 'all 0.15s ease', '&:hover': { bgcolor: alpha(opt.color, 0.04), borderColor: alpha(opt.color, 0.4) } }}>
                                                <Box sx={{ width: 28, height: 28, borderRadius: '6px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(opt.color, 0.12), color: opt.color }}>
                                                    <Iconify icon={opt.icon} width={16} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>{opt.label}</Typography>
                                                    <Typography sx={{ fontSize: '11px', color: '#888', lineHeight: 1.3 }}>{opt.subtitle}</Typography>
                                                </Box>
                                                {ftaOutcome === opt.key && (
                                                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, bgcolor: opt.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Iconify icon="mdi:check" width={12} sx={{ color: '#fff' }} />
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <Typography sx={{ fontSize: '13px', color: '#dc2626' }}>No booked appointment found.</Typography>
                            )}
                        </Box>
                    )}

                    {/* START_TREATMENT */}
                    {activeQuickAction === 'START_TREATMENT' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.5 }}>
                            <Typography sx={{ fontSize: '13px', color: '#555' }}>
                                Start treatment for <strong>{leadTreatment?.treatment || 'this lead'}</strong>
                            </Typography>
                            <DateTimePicker
                                label="Treatment date & time"
                                ampm={false}
                                value={quickActionDatetime}
                                minDateTime={new Date()}
                                onChange={(v) => setQuickActionDatetime(v)}
                                slotProps={{ textField: { fullWidth: true, size: 'small', sx: { '.MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', '& fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: ACCENT } } } } }}
                            />
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                {getDatePresets().filter((p) => p.value.isAfter(moment())).map((preset) => (
                                    <Chip
                                        key={preset.label}
                                        label={preset.label}
                                        size="small"
                                        variant={quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? 'filled' : 'outlined'}
                                        onClick={() => setQuickActionDatetime(preset.value.toDate())}
                                        sx={{ fontSize: '11px', fontWeight: 600, height: 26, borderRadius: '6px', borderColor: quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? ACCENT : '#ddd', bgcolor: quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? alpha(ACCENT, 0.1) : 'transparent', color: quickActionDatetime && moment(quickActionDatetime).isSame(preset.value, 'minute') ? ACCENT : '#666', '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.06) } }}
                                    />
                                ))}
                            </Box>
                            <FormControl fullWidth size="small">
                                <Select displayEmpty value={quickActionBookedWith} onChange={(e) => setQuickActionBookedWith(e.target.value)}
                                    renderValue={(val) => { if (!val) return <Typography sx={{ fontSize: '13px', color: '#aaa' }}>Booked with (optional)</Typography>; const u = practiceUserData?.results?.find((p) => p.id === val); return <Typography sx={{ fontSize: '13px' }}>{u?.user?.first_name} {u?.user?.last_name}</Typography>; }}
                                    sx={{ borderRadius: '8px', fontSize: '13px', '& fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: ACCENT } }}>
                                    <MenuItem value=""><Typography sx={{ fontSize: '13px', color: '#999' }}>None</Typography></MenuItem>
                                    {practiceUserData?.results?.filter((p) => !p?.user?.is_superuser && p?.is_enabled)?.map((p) => (
                                        <MenuItem key={p.id} value={p.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ width: 24, height: 24 }} src={p?.user?.avatar || ''} /><Typography sx={{ fontSize: '13px' }}>{p?.user?.first_name} {p?.user?.last_name}</Typography></Box></MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <Select displayEmpty value={quickActionAssignedTo} onChange={(e) => setQuickActionAssignedTo(e.target.value)}
                                    renderValue={(val) => { if (!val) return <Typography sx={{ fontSize: '13px', color: '#aaa' }}>Assigned to (optional)</Typography>; const u = practiceUserData?.results?.find((p) => p.id === val); return <Typography sx={{ fontSize: '13px' }}>{u?.user?.first_name} {u?.user?.last_name}</Typography>; }}
                                    sx={{ borderRadius: '8px', fontSize: '13px', '& fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: ACCENT } }}>
                                    <MenuItem value=""><Typography sx={{ fontSize: '13px', color: '#999' }}>None</Typography></MenuItem>
                                    {practiceUserData?.results?.filter((p) => !p?.user?.is_superuser && p?.is_enabled)?.map((p) => (
                                        <MenuItem key={p.id} value={p.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ width: 24, height: 24 }} src={p?.user?.avatar || ''} /><Typography sx={{ fontSize: '13px' }}>{p?.user?.first_name} {p?.user?.last_name}</Typography></Box></MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {isPaymentMandatory && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                                        <Typography sx={{ fontSize: '13px', color: '#555' }}>Deposit paid?</Typography>
                                        <Switch checked={depositConfirmed} onChange={(e) => { setDepositConfirmed(e.target.checked); if (!e.target.checked) setQuickActionPaymentMode(''); }} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
                                    </Box>
                                    {depositConfirmed && (
                                        <Box sx={{ px: 0.5 }}>
                                            <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.75 }}>How was it paid?</Typography>
                                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                                {[{ key: 'INPRACTICE', label: 'In Practice' }, { key: 'PHONE', label: 'Phone' }, { key: 'SEND', label: 'Send Link' }, { key: 'EMAIL', label: 'Email' }].map((pm) => (
                                                    <Chip key={pm.key} label={pm.label} size="small" variant={quickActionPaymentMode === pm.key ? 'filled' : 'outlined'} onClick={() => setQuickActionPaymentMode(pm.key)}
                                                        sx={{ fontSize: '11px', fontWeight: 600, height: 26, borderRadius: '6px', borderColor: quickActionPaymentMode === pm.key ? ACCENT : '#ddd', bgcolor: quickActionPaymentMode === pm.key ? alpha(ACCENT, 0.1) : 'transparent', color: quickActionPaymentMode === pm.key ? ACCENT : '#666', '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.06) } }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {/* COMPLETE */}
                    {activeQuickAction === 'COMPLETE' && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontSize: '13px', color: '#555' }}>
                                Complete treatment for {leadTreatment?.treatment || 'this lead'}?
                            </Typography>
                        </Box>
                    )}

                    {/* Cancel / Confirm */}
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch' }}>
                        <Button variant="outlined" sx={{ flex: 1, height: 44, fontSize: '14px', fontWeight: 600, borderRadius: '10px', color: '#666', borderColor: '#ddd' }} onClick={handleQuickActionCancel} disabled={quickActionLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={isQuickActionDisabled()}
                            sx={{ flex: 1, height: 44, fontSize: '14px', fontWeight: 600, borderRadius: '10px', bgcolor: `${ACCENT} !important`, color: '#fff', '&:hover': { bgcolor: '#005c3a !important' }, '&.Mui-disabled': { bgcolor: `${alpha(ACCENT, 0.4)} !important`, color: '#fff' } }}
                            startIcon={quickActionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check" width={16} />}
                            onClick={handleQuickActionConfirm}
                        >
                            {quickActionLoading ? 'Processing...' : 'Confirm'}
                        </Button>
                    </Box>
                </>
            ) : completedQuickAction ? (
                <>
                    {/* Success state */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: alpha(ACCENT, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Iconify icon="mdi:check" width={14} sx={{ color: ACCENT }} />
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: ACCENT }}>Done! What&apos;s next?</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                        {getNextStepOptions(completedQuickAction).map((opt) => (
                            <Box key={opt.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', border: '1px solid #f0f0f0', transition: 'all 0.15s ease', '&:hover': { bgcolor: alpha(opt.color, 0.06), borderColor: alpha(opt.color, 0.3) } }}>
                                <Box onClick={() => handleNextStep(opt.key)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, cursor: 'pointer' }}>
                                    <Box sx={{ width: 32, height: 32, borderRadius: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(opt.color, 0.12), color: opt.color }}>
                                        <Iconify icon={opt.icon} width={20} />
                                    </Box>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{opt.label}</Typography>
                                </Box>
                                {opt.key === 'GOOGLE_REVIEW' ? (
                                    <Tooltip title="Preview email template" placement="top" arrow>
                                        <Box onClick={handleOpenGoogleReviewPreview} sx={{ width: 28, height: 28, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { bgcolor: alpha(opt.color, 0.15) } }}>
                                            <Iconify icon="solar:eye-bold-duotone" width={18} sx={{ color: opt.color }} />
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    <Iconify icon="mdi:chevron-right" width={16} sx={{ color: '#ccc' }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                    <Button variant="outlined" fullWidth sx={{ height: 40, fontSize: '13px', fontWeight: 600, borderRadius: '8px', color: '#666', borderColor: '#ddd' }} onClick={handleQuickActionCancel}>
                        Close
                    </Button>
                </>
            ) : !showArchiveView ? (
                <>
                    {/* Treatment switcher — only visible when the lead has 2+ treatments.
                        Acts as the disambiguation mechanism so the user is never guessing
                        which treatment the action will dispatch against. */}
                    {showTreatmentSwitcher && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                            <Typography sx={{ fontSize: '12px', color: '#666', fontWeight: 500, flexShrink: 0 }}>
                                Acting on:
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                                <Select
                                    value={selectedTreatmentId || ''}
                                    onChange={(e) => setSelectedTreatmentId(e.target.value)}
                                    sx={{ fontSize: '13px', borderRadius: '8px', bgcolor: '#f8f8f8', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' } }}
                                >
                                    {availableTreatments.map((t) => (
                                        <MenuItem key={t.id} value={t.id} sx={{ fontSize: '13px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                    {t.treatment_name || t.treatment || `Treatment #${t.id}`}
                                                </Typography>
                                                <Chip
                                                    label={(t.status || 'NEW').replace(/_/g, ' ')}
                                                    size="small"
                                                    sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#eef', color: '#557', fontWeight: 600, ml: 1 }}
                                                />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Normal: Follow Up + Archive */}
                    <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                            {forwardActions.length === 0 && leadTreatment?.status === 'CLOSED' ? (
                                <ButtonGroup variant="contained" disableElevation sx={{ width: '100%', height: 44, borderRadius: '10px', boxShadow: `0px 4px 12px ${alpha('#FFAB00', 0.3)}`, '& .MuiButtonGroup-grouped': { border: 'none !important' } }}>
                                    <Button sx={{ bgcolor: '#FFAB00 !important', color: '#fff !important', fontSize: '14px', fontWeight: 600, flex: 1, borderRadius: '10px 0 0 10px !important', '&:hover': { bgcolor: '#B76E00 !important' } }} startIcon={<Iconify icon="solar:star-bold-duotone" width={16} />} onClick={() => handleNextStep('GOOGLE_REVIEW')}>
                                        Google Review
                                    </Button>
                                    <Tooltip title="Preview email" placement="top" arrow>
                                        <Button sx={{ bgcolor: '#FFAB00 !important', px: '12px !important', borderRadius: '0 10px 10px 0 !important', borderLeft: '2px solid rgba(255,255,255,0.4) !important', minWidth: 'auto !important', '&:hover': { bgcolor: '#B76E00 !important' } }} onClick={handleOpenGoogleReviewPreview}>
                                            <Iconify icon="solar:eye-bold" width={18} sx={{ color: '#fff' }} />
                                        </Button>
                                    </Tooltip>
                                </ButtonGroup>
                            ) : (
                                <>
                                    {/* Unified split-button SHAPE (mirrors statusPrimaryAction.js
                                        PRIMARY_BUTTON_SX / SPLIT_TRIGGER_SX) in CRM green. The
                                        WhatsApp channel state keeps its own #25D366; behaviour and
                                        handlers are unchanged from the previous ButtonGroup. */}
                                    {(() => {
                                        const isWa = taskMethod === 'WHATSAPP';
                                        const bg = isWa ? '#25D366' : ACCENT;
                                        const hover = isWa ? '#1da851' : '#00875a';
                                        const triggerHover = isWa ? '#1da851' : '#005c3a';
                                        const hasTrigger = hasDropdown || isWa;
                                        return (
                                            <Box ref={followUpAnchorRef} sx={{ display: 'inline-flex', alignItems: 'stretch', width: '100%' }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disableElevation
                                                    sx={sidebarPrimarySx(bg, hover, hasTrigger)}
                                                    startIcon={<Iconify icon={isWa ? 'eva:message-square-fill' : (singleAction?.icon || 'mdi:arrow-right')} width={14} />}
                                                    onClick={() => { if (isWa) { onSuccess('SEND_WHATSAPP'); } else if (singleAction) handleQuickAction(singleAction.key); else onSuccess('FOLLOW_UP'); }}
                                                >
                                                    {isWa ? 'Send WhatsApp' : (singleAction?.label || 'Follow Up')}
                                                </Button>
                                                {hasTrigger && (
                                                    <Tooltip title="Quick Actions" placement="bottom" arrow>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            disableElevation
                                                            aria-label="Quick Actions"
                                                            sx={sidebarTriggerSx(bg, triggerHover)}
                                                            onClick={() => setFollowUpMenuOpen((prev) => !prev)}
                                                        >
                                                            <Iconify icon="eva:arrow-ios-downward-fill" width={16} sx={{ color: '#fff' }} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        );
                                    })()}
                                    {/* Quick Actions dropdown — rendered via Popper to escape overflow:auto clipping */}
                                    <Popper
                                        open={followUpMenuOpen && (hasDropdown || taskMethod === 'WHATSAPP')}
                                        anchorEl={followUpAnchorRef.current}
                                        placement="bottom-start"
                                        style={{ zIndex: 1300, width: followUpAnchorRef.current?.offsetWidth }}
                                        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                                    >
                                        <ClickAwayListener onClickAway={() => setFollowUpMenuOpen(false)}>
                                            <Paper sx={{ borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f0f0f0', overflow: 'hidden' }}>
                                                {taskMethod === 'WHATSAPP' && (
                                                    <Box onClick={() => { onSuccess('MARK_COMPLETE_MANUAL_WS'); setFollowUpMenuOpen(false); }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { bgcolor: alpha('#25D366', 0.06) } }}>
                                                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#25D366', 0.12), color: '#25D366', flexShrink: 0 }}>
                                                            <Iconify icon="eva:checkmark-circle-2-fill" width={18} />
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>Sent manual WhatsApp</Typography>
                                                            <Typography sx={{ fontSize: '11px', color: '#888', lineHeight: 1.3 }}>Mark task complete without sending</Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                                {forwardActions.length > 0 ? forwardActions.map((action) => (
                                                    <Box key={action.key} onClick={() => handleQuickAction(action.key)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { bgcolor: alpha(action.color, 0.06) } }}>
                                                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(action.color, 0.12), color: action.color, flexShrink: 0 }}>
                                                            <Iconify icon={action.icon} width={18} />
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>{action.label}</Typography>
                                                            <Typography sx={{ fontSize: '11px', color: '#888', lineHeight: 1.3 }}>{action.subtitle}</Typography>
                                                        </Box>
                                                    </Box>
                                                )) : (
                                                    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <Iconify icon="mdi:information-outline" width={16} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                                                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.4 }}>
                                                            No quick actions for this lead status. Add a treatment first to access Book Consultation, Start / Complete Treatment.
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Paper>
                                        </ClickAwayListener>
                                    </Popper>
                                </>
                            )}
                        </Box>
                        {!lead?.is_archived && leadTreatment?.status !== 'TREATMENT_STARTED' && (
                            <Button variant="outlined" sx={{ height: 44, fontSize: '14px', fontWeight: 600, borderRadius: '10px', minWidth: 'auto', px: 2, color: '#FF5630', borderColor: '#FF5630', '&:hover': { borderColor: '#B71D18', bgcolor: alpha('#FF5630', 0.08) } }} startIcon={<Iconify icon="eva:archive-fill" width={16} />} onClick={() => setShowArchiveView(true)}>
                                Archive
                            </Button>
                        )}
                    </Box>
                </>
            ) : (
                <>
                    {/* Archive form */}
                    <Box onClick={() => { setShowArchiveView(false); setArchiveSubmitting(false); setArchiveReason(''); setArchiveNotes(''); }} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 2, color: '#888', '&:hover': { color: '#444' }, transition: 'color 0.15s ease' }}>
                        <Iconify icon="mdi:arrow-left" width={18} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>Back</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, bgcolor: '#fff4f2', borderRadius: '12px', mb: 2, border: '1px solid #ffe4de' }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, #FF5630 0%, #FF8660 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Iconify icon="eva:archive-fill" width={20} sx={{ color: '#fff' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#B71D18' }}>Archive {patientName}</Typography>
                            <Typography sx={{ fontSize: '12px', color: '#FF5630', mt: 0.25 }}>{leadTreatment?.treatment || 'No treatment'}</Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#444', mb: 1.25 }}>Why are you archiving?</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                        {archiveReasonsList.map((reason) => {
                            const selected = archiveReason === reason.value;
                            return (
                                <Box key={reason.value} onClick={() => setArchiveReason(reason.value)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1.5, borderRadius: '10px', cursor: 'pointer', border: '1.5px solid', borderColor: selected ? '#FF5630' : '#e8e8e8', bgcolor: selected ? '#FFF4F2' : '#fafafa', transition: 'all 0.15s ease', '&:hover': { borderColor: selected ? '#FF5630' : '#ccc', bgcolor: selected ? '#FFF4F2' : '#f5f5f5' } }}>
                                    <Iconify icon={ARCHIVE_REASON_ICONS[reason.value] || 'mdi:help-circle-outline'} width={22} sx={{ color: selected ? '#FF5630' : '#999' }} />
                                    <Typography sx={{ fontSize: '11px', fontWeight: selected ? 600 : 500, color: selected ? '#B71D18' : '#555', textAlign: 'center', lineHeight: 1.3, px: 0.5 }}>{reason.label}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#444', mb: 1 }}>Notes</Typography>
                    <TextField multiline rows={3} placeholder="Add archive notes..." value={archiveNotes} onChange={(e) => setArchiveNotes(e.target.value)} sx={{ width: '100%', mb: 2, '.MuiOutlinedInput-root': { bgcolor: '#fafafa', borderRadius: '10px', fontSize: '13px', '& fieldset': { borderColor: '#e8e8e8' }, '&:hover fieldset': { borderColor: '#ccc' }, '&.Mui-focused fieldset': { borderColor: '#FF5630' }, '&.Mui-focused': { bgcolor: '#fff' } } }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#444', mb: 1 }}>Open to future contact?</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        {['Yes', 'No'].map((val) => (
                            <Box key={val} onClick={() => setArchiveCommunication(val)} sx={{ px: 2.5, py: 0.875, borderRadius: '10px', cursor: 'pointer', border: '1.5px solid', borderColor: archiveCommunication === val ? ACCENT : '#e8e8e8', bgcolor: archiveCommunication === val ? alpha(ACCENT, 0.08) : '#fafafa', transition: 'all 0.15s ease', '&:hover': { borderColor: archiveCommunication === val ? ACCENT : '#ccc' } }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: archiveCommunication === val ? 600 : 500, color: archiveCommunication === val ? ACCENT : '#555' }}>{val}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Button variant="outlined" sx={{ flex: 1, height: 44, fontSize: '14px', fontWeight: 600, borderRadius: '10px', color: '#666', borderColor: '#ddd' }} onClick={() => setShowArchiveView(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={archiveSubmitting}
                            sx={{ flex: 1, height: 44, fontSize: '14px', fontWeight: 600, borderRadius: '10px', bgcolor: '#FF5630', '&:hover': { bgcolor: '#B71D18' }, '&.Mui-disabled': { bgcolor: alpha('#FF5630', 0.5), color: '#fff' } }}
                            startIcon={<Iconify icon={archiveSubmitting ? 'mdi:loading' : 'eva:archive-fill'} width={16} sx={archiveSubmitting ? { animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } } : {}} />}
                            onClick={handleArchiveSubmit}
                        >
                            {archiveSubmitting ? 'Archiving...' : 'Archive'}
                        </Button>
                    </Box>
                </>
            )}

            {googleReviewPreviewOpen && (
                <GoogleReviewPreviewDialog
                    open={googleReviewPreviewOpen}
                    onClose={handleCloseGoogleReviewPreview}
                    templateData={googleReviewTemplateDetail}
                />
            )}
        </Box>
    );
};

LeadActionZone.propTypes = {
    lead: PropTypes.object,
    leadTreatment: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSuccess: PropTypes.func,
    taskMethod: PropTypes.string,
    onActionComplete: PropTypes.func,
    bookRoutesToWizard: PropTypes.bool,
};

LeadActionZone.defaultProps = {
    lead: null,
    leadTreatment: null,
    practiceId: null,
    onSuccess: () => {},
    onActionComplete: null,
    bookRoutesToWizard: false,
};

export default LeadActionZone;
